import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'

/**
 * Theme Context
 *
 * Manages light/dark theme with persistence.
 * - Defaults to dark theme (the "deep space" default)
 * - Allows manual toggle to light theme
 * - Persists user choice in localStorage
 */

const STORAGE_KEY = 'theme-preference'

const ThemeContext = createContext(null)

export const ThemeProvider = ({ children }) => {
  // Get stored preference or default to dark theme
  const getInitialState = useCallback(() => {
    if (typeof window === 'undefined') return { theme: 'dark', source: 'default' }

    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        return { theme: parsed.theme, source: 'user' }
      }
    } catch (e) {
      console.warn('Failed to read theme preference:', e)
    }

    // Default: dark theme
    return { theme: 'dark', source: 'default' }
  }, [])

  const [preferences, setPreferences] = useState(getInitialState)

  // Toggle theme
  const toggleTheme = useCallback(() => {
    setPreferences(prev => {
      const newTheme = prev.theme === 'light' ? 'dark' : 'light'
      const newState = { theme: newTheme, source: 'user' }
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newState))
      } catch (e) {
        console.warn('Failed to save theme preference:', e)
      }
      return newState
    })
  }, [])

  // Set specific theme
  const setTheme = useCallback((theme) => {
    const newState = { theme, source: 'user' }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newState))
    } catch (e) {
      console.warn('Failed to save theme preference:', e)
    }
    setPreferences(newState)
  }, [])

  // Reset to default (dark theme)
  const resetToDefault = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch (e) {
      console.warn('Failed to clear theme preference:', e)
    }
    setPreferences({ theme: 'dark', source: 'default' })
  }, [])

  // Apply CSS class to document for theme switching
  useEffect(() => {
    const root = document.documentElement
    if (preferences.theme === 'dark') {
      root.classList.add('dark-theme')
      root.classList.remove('light-theme')
    } else {
      root.classList.add('light-theme')
      root.classList.remove('dark-theme')
    }
  }, [preferences.theme])

  const value = {
    theme: preferences.theme,
    isDark: preferences.theme === 'dark',
    source: preferences.source,
    toggleTheme,
    setTheme,
    resetToDefault
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider')
  }
  return context
}

export default ThemeContext
